import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Drawer from "@mui/material/Drawer";
import { Stack, Box, Typography, Grid, IconButton, MenuItem, FormControl, InputLabel, Select, FormHelperText, Accordion, AccordionSummary, AccordionDetails, Autocomplete, Tabs, Tab } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BookingCycle from '../../components/BookingCycle';
import FieldSearch, { FieldSearchConfig } from "../../components/FieldSearch";
import { useForm, Controller } from "react-hook-form";
import { useBookingStore } from "../../services/bookings";
import { useSlotStore } from "../../services/slots";
import {
    CreateWellnessPackageBookingRequest,
    CreateSpecialistBookingRequest,
    UpdateBookingRequest,
    IBooking,
    IDocument,
    PendingBookingDocument,
    IServiceAddress,
    BookingPaymentDetail,
    BookingWalletDetail,
    BookingRefundDetail,
    BookingPaymentSummary,
    BookingDetailPayload
} from "../../types/bookings";
import { ISlot } from "../../types/slots";
import { WellnessPackage } from "../../types/WellnessPackage";
import { Specialist } from "../../types/specialist";
import { UserData } from "../../types/user";
import { BOOKING_TYPES, SERVICE_MODES, CONSULTATION_MODES, BOOKING_STATUSES, PAYMENT_STATUSES } from "./constants";
import dayjs from "dayjs";
import { doGET } from "../../utils/HttpUtils";
import { showError } from "../../services/toaster";
import PaginatedSearchDropdown, { PaginatedOption } from "../../components/PaginatedSearchDropdown";
import { useSurgeryStore } from "../../services/surgery";
import { useSecondOpinionStore } from "../../services/secondOpinion";
import { useWellnessPackageStore } from "../../services/wellnessPackages";
import { uploadFile } from "../../utils/helper";
import { MODULES } from "../../utils/constants";

interface BookingFormValues {
    bookingType: 'package' | 'consultation' | 'surgery' | 'second-opinion';
    bookingDate: string;
    bookingTime: string;
    serviceMode: string;
    status: string;
    paymentStatus: string;
    price: number;
    documents: IDocument[];
    slotId: string;
    userId?: string;
    dependentId?: string;
    corporateId?: string;
    wellnessPackageId?: string;
    specialistId?: string;
    primaryConcern?: string;
    consultationMode?: string;
    contactNo?: string;
    alternateContactNo?: string;
    serviceAddress?: IServiceAddress;
    notes?: string;
    cancellationReason?: string;
    [key: string]: any;
}

interface DependentDetails {
    _id?: string;
    name?: string;
    phone?: string;
    email?: string;
    gender?: string;
    relation?: string;
    relationship?: string;
    age?: number | string;
}

const FILE_BASE_URL = "https://api.myewacare.com";

const bookingSearchFields: FieldSearchConfig[] = [
    {
        fieldName: "search",
        label: "All",
        inputType: "text",
        placeholder: "Search by mobile, email, or name",
    },
    {
        fieldName: "mobile",
        label: "Mobile Number",
        inputType: "tel",
        placeholder: "Enter mobile number",
    },
    {
        fieldName: "email",
        label: "Email ID",
        inputType: "email",
        placeholder: "Enter email ID",
    },
    {
        fieldName: "name",
        label: "Name",
        inputType: "text",
        placeholder: "Enter name",
    },
];

export default function AddBookingDialog({
    isModalOpen,
    toggleModal,
    selectedId,
    hideAddButton = false
}: any) {
    const { onCreateSpecialist, onCreateWellnessPackage, detail, onUpdate, filters, setFilters, data: bookingsList } = useBookingStore();
    const { fetchBySpecialist, fetchByWellnessPackage } = useSlotStore();
    const { onCreate: onCreateSurgery } = useSurgeryStore();
    const { onCreate: onCreateSecondOpinion } = useSecondOpinionStore();
    const { data: wellnessPackages, fetchGrid: fetchWellnessPackages } = useWellnessPackageStore();
    const [specialists, setSpecialists] = React.useState<Specialist[]>([]);

    React.useEffect(() => {
        fetchWellnessPackages();
    }, []);

    const fetchAllSpecialists = React.useCallback(async () => {
        try {
            const allSpecialists: Specialist[] = [];
            const seenIds = new Set<string>();
            let page = 1;
            let totalPages = 1;
            const limit = 100;

            do {
                const response = await doGET(`/specialists?page=${page}&limit=${limit}`);
                if (!(response.status >= 200 && response.status < 400)) {
                    break;
                }

                const rows = Array.isArray(response.data?.data) ? response.data.data : [];
                totalPages = Number(response.data?.pagination?.totalPages) || 1;

                rows.forEach((specialist: Specialist) => {
                    if (specialist._id && !seenIds.has(specialist._id)) {
                        seenIds.add(specialist._id);
                        allSpecialists.push(specialist);
                    }
                });

                page += 1;
            } while (page <= totalPages);

            setSpecialists(allSpecialists);
        } catch (error) {
            console.error('Failed to fetch all specialists for booking filters', error);
        }
    }, []);

    React.useEffect(() => {
        fetchAllSpecialists();
    }, [fetchAllSpecialists]);

    const [bookingType, setBookingType] = React.useState<'package' | 'consultation' | 'surgery' | 'second-opinion'>('package');
    const [availableSlots, setAvailableSlots] = React.useState<ISlot[]>([]);
    const [loadingSlots, setLoadingSlots] = React.useState(false);
    const [bookingScope, setBookingScope] = React.useState<'general' | 'corporate' | ''>('');
    const [userLabel, setUserLabel] = React.useState<string>('');
    const [selectedUser, setSelectedUser] = React.useState<UserData | null>(null);
    const [selectedDependent, setSelectedDependent] = React.useState<DependentDetails | null>(null);
    const [packageLabel, setPackageLabel] = React.useState<string>('');
    const [specialistLabel, setSpecialistLabel] = React.useState<string>('');
    const [pendingDocuments, setPendingDocuments] = React.useState<PendingBookingDocument[]>([]);
    const [paymentDetail, setPaymentDetail] = React.useState<BookingPaymentDetail | null>(null);
    const [walletDetail, setWalletDetail] = React.useState<BookingWalletDetail | null>(null);
    const [refundDetail, setRefundDetail] = React.useState<BookingRefundDetail | null>(null);
    const [paymentSummary, setPaymentSummary] = React.useState<BookingPaymentSummary | null>(null);
    const [cycleBooking, setCycleBooking] = React.useState<IBooking | null>(null);
    const [activeTab, setActiveTab] = React.useState('details');
    const cycleRequestRef = React.useRef(0);
    const corporatePackagesCacheRef = React.useRef<Record<string, WellnessPackage[]>>({});
    const corporateSpecialistsCacheRef = React.useRef<Record<string, Specialist[]>>({});
    const specialistFilterOptions = React.useMemo(
        () =>
            specialists.map((specialist) => ({
                value: specialist._id || '',
                label: `${specialist.name || 'Unknown'}${specialist.specialization ? ` (${specialist.specialization})` : ''}`,
            })),
        [specialists]
    );
    const packageFilterOptions = React.useMemo(
        () =>
            wellnessPackages.map((pkg) => ({
                value: pkg._id || '',
                label: pkg.name || 'Unnamed Package',
            })),
        [wellnessPackages]
    );

    const defaultValues: BookingFormValues = {
        bookingScope: '',
        bookingType: 'package' as const,
        bookingDate: dayjs().format('YYYY-MM-DD'),
        bookingTime: "09:00",
        serviceMode: 'in-person',
        status: 'pending',
        paymentStatus: 'pending',
        price: 0,
        documents: [],
        slotId: '',
    };

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors }
    } = useForm<BookingFormValues>({
        defaultValues: defaultValues
    });

    const watchedBookingType = watch('bookingType');
    const watchedCorporateId = watch('corporateId');
    const watchedUserId = watch('userId');
    const watchedSpecialistId = watch('specialistId');
    const watchedWellnessPackageId = watch('wellnessPackageId');
    const watchedDocuments = watch('documents') || [];
    const canOpenCreateForm = Boolean(selectedId) || bookingScope === 'general' || (bookingScope === 'corporate' && Boolean(watchedCorporateId));
    const isSlotPrerequisiteSelected =
        bookingType === 'consultation'
            ? Boolean(watchedSpecialistId)
            : bookingType === 'package'
                ? Boolean(watchedWellnessPackageId)
                : true; // surgery and second-opinion don't need slots
    const mapToOption = (value?: string, label?: string): PaginatedOption | null => {
        if (!value) return null;
        return {
            value,
            label: label || value,
        };
    };

    const renderDetailAccordion = React.useCallback((
        title: string,
        content: React.ReactNode,
        defaultExpanded = false
    ) => (
        <Accordion
            defaultExpanded={defaultExpanded}
            disableGutters
            sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'grey.50',
                boxShadow: 'none',
                '&:before': { display: 'none' },
            }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {title}
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
                {content}
            </AccordionDetails>
        </Accordion>
    ), []);

    const formatCurrency = React.useCallback((amount?: number, currency = "INR") => {
        if (amount === undefined || amount === null || Number.isNaN(Number(amount))) return "-";
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency,
            maximumFractionDigits: 0,
        }).format(Number(amount));
    }, []);

    const formatDateTime = React.useCallback((value?: string) => {
        if (!value) return "-";
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed.format("DD MMM YYYY, hh:mm A") : value;
    }, []);

    const resolveDocumentUrl = React.useCallback((url?: string) => {
        if (!url) return "#";
        return url.startsWith("http") ? url : `${FILE_BASE_URL}${url}`;
    }, []);

    const fetchCorporatePackages = React.useCallback(async (corporateId: string) => {
        if (!corporateId) return [];
        if (corporatePackagesCacheRef.current[corporateId]) {
            return corporatePackagesCacheRef.current[corporateId];
        }
        const response = await doGET(`/corporates/${corporateId}/wellness-packages`);
        const rows = response?.data?.data;
        const packages = Array.isArray(rows) ? rows : [];
        corporatePackagesCacheRef.current[corporateId] = packages;
        return packages;
    }, []);

    const fetchCorporateSpecialists = React.useCallback(async (corporateId: string) => {
        if (!corporateId) return [];
        if (corporateSpecialistsCacheRef.current[corporateId]) {
            return corporateSpecialistsCacheRef.current[corporateId];
        }
        const response = await doGET(`/corporates/${corporateId}/specialists`);
        const rows = response?.data?.data;
        const specialists = Array.isArray(rows) ? rows : [];
        corporateSpecialistsCacheRef.current[corporateId] = specialists;
        return specialists;
    }, []);

    const fetchCorporateOptions = React.useCallback(async ({ page, limit, search }: { page: number; limit: number; search: string; }) => {
        const params = new URLSearchParams();
        params.append("page", String(page));
        params.append("limit", String(limit));
        if (search) params.append("search", search);

        const response = await doGET(`/corporates?${params.toString()}`);
        const rows = response?.data?.data;
        const pagination = response?.data?.pagination;
        const corporates = Array.isArray(rows) ? rows : [];
        const options = corporates
            .map((item: any) => mapToOption(item?._id, item?.name))
            .filter(Boolean) as PaginatedOption[];
        const hasMore = pagination?.totalPages ? page < pagination.totalPages : corporates.length === limit;
        return { options, hasMore };
    }, []);

    const fetchUserOptions = React.useCallback(async ({ page, limit, search }: { page: number; limit: number; search: string; }) => {
        const params = new URLSearchParams();
        params.append("page", String(page));
        params.append("limit", String(limit));
        if (search) params.append("name", search);

        if (bookingScope === "corporate" && watchedCorporateId) {
            params.append("corporateId", watchedCorporateId);
        }

        const response = await doGET(`/users?${params.toString()}`);
        const rows = response?.data?.data?.users ?? response?.data?.data ?? [];
        const users = Array.isArray(rows) ? rows : [];
        const options = users
            .map((item: any) => mapToOption(item?._id, item?.name || item?.email || item?.phone))
            .filter(Boolean) as PaginatedOption[];
        const hasMore = users.length === limit;
        return { options, hasMore };
    }, [bookingScope, watchedCorporateId]);

    const fetchPackageOptions = React.useCallback(async ({ page, limit, search }: { page: number; limit: number; search: string; }) => {
        if (bookingScope === "corporate" && watchedCorporateId) {
            const corporatePackages = await fetchCorporatePackages(watchedCorporateId);
            const filtered = corporatePackages.filter((item) =>
                (item.name || "").toLowerCase().includes(search.toLowerCase())
            );
            const start = (page - 1) * limit;
            const paginated = filtered.slice(start, start + limit);
            const options = paginated
                .map((item) => mapToOption(item?._id, item?.name))
                .filter(Boolean) as PaginatedOption[];
            return { options, hasMore: start + limit < filtered.length };
        }

        const params = new URLSearchParams();
        params.append("page", String(page));
        params.append("limit", String(limit));
        if (search) params.append("search", search);

        const response = await doGET(`/wellness-packages?${params.toString()}`);
        const rows = response?.data?.data;
        const pagination = response?.data?.pagination;
        const packages = Array.isArray(rows) ? rows : [];
        const options = packages
            .map((item: any) => mapToOption(item?._id, item?.name))
            .filter(Boolean) as PaginatedOption[];
        const hasMore = pagination?.totalPages ? page < pagination.totalPages : packages.length === limit;
        return { options, hasMore };
    }, [bookingScope, watchedCorporateId, fetchCorporatePackages]);

    const fetchSpecialistOptions = React.useCallback(async ({ page, limit, search }: { page: number; limit: number; search: string; }) => {
        if (bookingScope === "corporate" && watchedCorporateId) {
            const corporateSpecialists = await fetchCorporateSpecialists(watchedCorporateId);
            const filtered = corporateSpecialists.filter((item) => {
                const title = `${item.name || ""} ${item.specialization || ""}`.toLowerCase();
                return title.includes(search.toLowerCase());
            });
            const start = (page - 1) * limit;
            const paginated = filtered.slice(start, start + limit);
            const options = paginated
                .map((item) => mapToOption(item?._id, `${item?.name || "Unknown"}${item?.specialization ? ` (${item.specialization})` : ""}`))
                .filter(Boolean) as PaginatedOption[];
            return { options, hasMore: start + limit < filtered.length };
        }

        const params = new URLSearchParams();
        params.append("page", String(page));
        params.append("limit", String(limit));
        if (search) params.append("search", search);

        const response = await doGET(`/specialists?${params.toString()}`);
        const rows = response?.data?.data;
        const pagination = response?.data?.pagination;
        const specialists = Array.isArray(rows) ? rows : [];
        const options = specialists
            .map((item: any) => mapToOption(item?._id, `${item?.name || "Unknown"}${item?.specialization ? ` (${item.specialization})` : ""}`))
            .filter(Boolean) as PaginatedOption[];
        const hasMore = pagination?.totalPages ? page < pagination.totalPages : specialists.length === limit;
        return { options, hasMore };
    }, [bookingScope, watchedCorporateId, fetchCorporateSpecialists]);

    const fetchSlotOptions = React.useCallback(async ({ page, limit, search }: { page: number; limit: number; search: string; }) => {
        const filteredSlots = availableSlots.filter((slot) => {
            const label = formatSlotLabel(slot).toLowerCase();
            return label.includes(search.toLowerCase());
        });
        const start = (page - 1) * limit;
        const paginated = filteredSlots.slice(start, start + limit);
        const options = paginated.map((slot) => ({
            value: slot._id || "",
            label: formatSlotLabel(slot),
        }));
        return {
            options,
            hasMore: start + limit < filteredSlots.length,
        };
    }, [availableSlots]);

    React.useEffect(() => {
        setBookingType(watchedBookingType);
        // Clear slots and slotId when booking type changes
        setAvailableSlots([]);
        setValue('slotId', '');
    }, [watchedBookingType]);

    React.useEffect(() => {
        if (!isModalOpen || selectedId) return;

        setValue('userId', '');
        setSelectedUser(null);
        setSelectedDependent(null);
        setUserLabel('');
        setValue('wellnessPackageId', '');
        setValue('specialistId', '');
        setValue('slotId', '');
        setAvailableSlots([]);

        if (bookingScope === 'general') {
            setValue('corporateId', undefined);
        }
    }, [bookingScope, watchedCorporateId, isModalOpen, selectedId, setValue]);

    // Fetch available slots when specialistId changes (for consultation type)
    React.useEffect(() => {
        if (bookingType === 'consultation' && watchedSpecialistId && !selectedId) {
            const fetchSlots = async () => {
                setLoadingSlots(true);
                try {
                    const slots = await fetchBySpecialist(watchedSpecialistId);
                    setAvailableSlots(Array.isArray(slots) ? slots : []);
                } catch (error) {
                    console.error('Failed to fetch slots for specialist', error);
                    setAvailableSlots([]);
                } finally {
                    setLoadingSlots(false);
                }
            };
            fetchSlots();
            setValue('slotId', '');
        }
    }, [watchedSpecialistId, bookingType]);

    // Fetch available slots when wellnessPackageId changes (for package type)
    React.useEffect(() => {
        if (bookingType === 'package' && watchedWellnessPackageId && !selectedId) {
            const fetchSlots = async () => {
                setLoadingSlots(true);
                try {
                    const slots = await fetchByWellnessPackage(watchedWellnessPackageId);
                    setAvailableSlots(Array.isArray(slots) ? slots : []);
                } catch (error) {
                    console.error('Failed to fetch slots for wellness package', error);
                    setAvailableSlots([]);
                } finally {
                    setLoadingSlots(false);
                }
            };
            fetchSlots();
            setValue('slotId', '');
        }
    }, [watchedWellnessPackageId, bookingType]);

    React.useEffect(() => {
        if (!isModalOpen || !watchedUserId) {
            if (!watchedUserId) {
                setSelectedUser(null);
                if (!selectedId) {
                    setUserLabel('');
                }
            }
            return;
        }

        const fetchSelectedUser = async () => {
            try {
                const response = await doGET(`/users/${watchedUserId}`);
                const raw = response?.data;
                const user = raw?.data?.user || raw?.user || raw?.data || raw;

                if (user) {
                    setSelectedUser(user);
                    setUserLabel(user.name || user.email || user.phone || watchedUserId);
                }
            } catch (error) {
                console.error('Failed to fetch user details', error);
            }
        };

        fetchSelectedUser();
    }, [watchedUserId, isModalOpen, selectedId]);

    // Auto-fill bookingDate and bookingTime when a slot is selected
    const watchedSlotId = watch('slotId');
    React.useEffect(() => {
        if (watchedSlotId && availableSlots.length > 0) {
            const selectedSlot = availableSlots.find(s => s._id === watchedSlotId);
            if (selectedSlot) {
                // Convert slot date YYYYMMDD to YYYY-MM-DD for the date input
                const slotDate = selectedSlot.date;
                if (slotDate && slotDate.length === 8) {
                    const formatted = `${slotDate.substring(0, 4)}-${slotDate.substring(4, 6)}-${slotDate.substring(6, 8)}`;
                    setValue('bookingDate', formatted);
                }
                if (selectedSlot.startTime) {
                    setValue('bookingTime', selectedSlot.startTime);
                }
            }
        }
    }, [watchedSlotId, availableSlots]);


    const handleClickOpen = () => {
        cycleRequestRef.current += 1;
        setCycleBooking(null);
        reset(defaultValues);
        setBookingScope('');
        setAvailableSlots([]);
        setSelectedUser(null);
        setSelectedDependent(null);
        setUserLabel('');
        setPackageLabel('');
        setSpecialistLabel('');
        setPendingDocuments([]);
        setPaymentDetail(null);
        setWalletDetail(null);
        setRefundDetail(null);
        setPaymentSummary(null);
        toggleModal(true);
    };

    const handleClose = () => {
        cycleRequestRef.current += 1;
        setCycleBooking(null);
        toggleModal(false);
        reset(defaultValues);
        setBookingScope('');
        setAvailableSlots([]);
        setSelectedUser(null);
        setSelectedDependent(null);
        setUserLabel('');
        setPackageLabel('');
        setSpecialistLabel('');
        setPendingDocuments([]);
        setPaymentDetail(null);
        setWalletDetail(null);
        setRefundDetail(null);
        setPaymentSummary(null);
    };

    const addDocumentEntry = React.useCallback(() => {
        setPendingDocuments((prev) => [
            ...prev,
            { type: 'report', file: null, fileName: '' },
        ]);
    }, []);

    const updatePendingDocument = React.useCallback((
        index: number,
        key: keyof PendingBookingDocument,
        value: PendingBookingDocument[keyof PendingBookingDocument]
    ) => {
        setPendingDocuments((prev) =>
            prev.map((doc, docIndex) =>
                docIndex === index ? { ...doc, [key]: value } : doc
            )
        );
    }, []);

    const uploadBookingDocuments = React.useCallback(async (bookingId: string, existingDocuments: IDocument[]) => {
        const documentsToUpload = pendingDocuments.filter((doc) => doc.file);

        if (!documentsToUpload.length) {
            return existingDocuments;
        }

        const uploadRes = await uploadFile(
            { module: MODULES.BOOKING, record_id: bookingId },
            documentsToUpload.map((doc) => doc.file as File)
        );

        if (!(uploadRes.status >= 200 && uploadRes.status < 400)) {
            throw new Error(uploadRes?.message || 'Failed to upload booking documents');
        }

        const uploadedPaths = Array.isArray(uploadRes.data?.data) ? uploadRes.data.data : [];
        const uploadedDocuments: IDocument[] = documentsToUpload
            .map((doc, index) => ({
                type: doc.type,
                url: uploadedPaths[index] || '',
                fileName: doc.fileName || doc.file?.name || '',
            }))
            .filter((doc) => Boolean(doc.url));

        return [...existingDocuments, ...uploadedDocuments];
    }, [pendingDocuments]);

    const onSubmit = async (data: any) => {
        if (!selectedId && !bookingScope) {
            showError('Please select booking scope');
            return;
        }

        if (!selectedId && bookingScope === 'corporate' && !data.corporateId) {
            showError('Please select corporate for corporate booking');
            return;
        }

        const formattedDate = data.bookingDate ? data.bookingDate.replace(/-/g, '') : '';

        const payload: any = {
            ...data,
            bookingDate: formattedDate,
            price: Number(data.price),
            documents: watchedDocuments,
        };
        delete payload.bookingScope;

        if (selectedId) {
            delete payload.userId;
            delete payload.wellnessPackageId;
            delete payload.specialistId;
        } else if (bookingScope === 'general') {
            delete payload.corporateId;
        }

        try {
            if (selectedId) {
                const updatePayload: UpdateBookingRequest = {
                    corporateId: payload.corporateId,
                    bookingDate: payload.bookingDate,
                    bookingTime: payload.bookingTime,
                    serviceMode: payload.serviceMode,
                    serviceAddress: payload.serviceAddress,
                    documents: payload.documents,
                    primaryConcern: payload.primaryConcern,
                    consultationMode: payload.consultationMode,
                    contactNo: payload.contactNo,
                    alternateContactNo: payload.alternateContactNo,
                    status: payload.status,
                    paymentStatus: payload.paymentStatus,
                    notes: payload.notes,
                    cancellationReason: payload.cancellationReason
                };
                await onUpdate(selectedId, updatePayload);

                if (pendingDocuments.some((doc) => doc.file)) {
                    const mergedDocuments = await uploadBookingDocuments(selectedId, payload.documents || []);
                    await onUpdate(selectedId, { documents: mergedDocuments });
                }
            } else {
                let createdBooking: IBooking | null = null;

                if (bookingType === 'package') {
                    createdBooking = await onCreateWellnessPackage(payload as CreateWellnessPackageBookingRequest);
                } else if (bookingType === 'consultation') {
                    createdBooking = await onCreateSpecialist(payload as CreateSpecialistBookingRequest);
                } else if (bookingType === 'surgery') {
                    const surgeryPayload = {
                        full_name: payload.full_name || '',
                        email: payload.email || '',
                        concern: payload.primaryConcern || payload.concern || '',
                        speciality: payload.speciality || '',
                        medical_record: payload.medical_record || [],
                    };
                    await onCreateSurgery(surgeryPayload);
                } else if (bookingType === 'second-opinion') {
                    const secondOpinionPayload = {
                        full_name: payload.full_name || '',
                        email: payload.email || '',
                        concern: payload.primaryConcern || payload.concern || '',
                        medical_record: payload.medical_record || [],
                    };
                    await onCreateSecondOpinion(secondOpinionPayload);
                }

                if (createdBooking?._id && pendingDocuments.some((doc) => doc.file)) {
                    const mergedDocuments = await uploadBookingDocuments(createdBooking._id, payload.documents || []);
                    await onUpdate(createdBooking._id, { documents: mergedDocuments });
                }
            }

            handleClose();
        } catch (error) {
            console.error('Failed to submit booking', error);
            showError('Failed to save booking');
        }
    };

    const fetchDetail = async (id: string) => {
        const requestId = ++cycleRequestRef.current;
        setCycleBooking(null);
        try {
            setPendingDocuments([]);
            const response = await detail(id);
            if (response?.data) {
                const detailData = response.data as IBooking | BookingDetailPayload | Record<string, any>;
                const bookingData = (detailData as BookingDetailPayload).booking ?? detailData;
                if (requestId === cycleRequestRef.current) setCycleBooking(bookingData as IBooking);

                const date = bookingData.bookingDate;
                const formattedDate = date && date.length === 8
                    ? `${date.substring(0, 4)}-${date.substring(4, 6)}-${date.substring(6, 8)}`
                    : date;

                reset({
                    ...bookingData,
                    bookingDate: formattedDate,
                });
                setBookingType(bookingData.bookingType);
                // Infer booking scope for existing bookings so dropdowns work correctly
                if (bookingData.corporateId) {
                    setBookingScope('corporate');
                } else {
                    setBookingScope('general');
                }

                // Fetch display labels for user, wellness package, and specialist for edit view (in parallel)
                const fetchLabels = async () => {
                    const tasks: Promise<any>[] = [];

                    if (bookingData.userId) {
                        tasks.push(doGET(`/users/${bookingData.userId}`));
                    } else {
                        tasks.push(Promise.resolve(null));
                    }

                    if (bookingData.wellnessPackageId) {
                        tasks.push(doGET(`/wellness-packages/${bookingData.wellnessPackageId}`));
                    } else {
                        tasks.push(Promise.resolve(null));
                    }

                    if (bookingData.specialistId) {
                        tasks.push(doGET(`/specialists/${bookingData.specialistId}`));
                    } else {
                        tasks.push(Promise.resolve(null));
                    }

                    if (bookingData.dependentId) {
                        tasks.push(doGET(`/dependents/${bookingData.dependentId}`));
                    } else {
                        tasks.push(Promise.resolve(null));
                    }

                    const [userResult, packageResult, specialistResult, dependentResult] = await Promise.allSettled(tasks);

                    if (userResult.status === 'fulfilled' && userResult.value) {
                        const raw = userResult.value.data;
                        const u = raw?.data?.user || raw?.user || raw;
                        if (u) {
                            setSelectedUser(u);
                            setUserLabel(u.name || u.email || u.phone || bookingData.userId);
                        }
                    }

                    if (packageResult.status === 'fulfilled' && packageResult.value) {
                        const raw = packageResult.value.data;
                        const p = raw?.data || raw;
                        if (p) {
                            setPackageLabel(p.name || bookingData.wellnessPackageId);
                        }
                    }

                    if (specialistResult.status === 'fulfilled' && specialistResult.value) {
                        const raw = specialistResult.value.data;
                        const s = raw?.data || raw;
                        if (s) {
                            const base = s.name || 'Unknown';
                            setSpecialistLabel(s.specialization ? `${base} (${s.specialization})` : base);
                        }
                    }

                    if (dependentResult.status === 'fulfilled' && dependentResult.value) {
                        const raw = dependentResult.value.data;
                        const dependent = raw?.data?.dependent || raw?.dependent || raw?.data || raw;
                        if (dependent) {
                            setSelectedDependent(dependent);
                        }
                    }
                };

                fetchLabels();
            }

            const paymentResponse = await doGET(`/bookings/${id}/payment-details`);
            const paymentPayload = paymentResponse?.data?.data || paymentResponse?.data;

            if (paymentPayload) {
                setPaymentDetail(paymentPayload.payment ?? null);
                setWalletDetail(paymentPayload.wallet ?? null);
                setRefundDetail(paymentPayload.refund ?? null);
                setPaymentSummary(paymentPayload.summary ?? null);
            } else {
                setPaymentDetail(null);
                setWalletDetail(null);
                setRefundDetail(null);
                setPaymentSummary(null);
            }
        } catch (error) {
            console.error(error);
        }
    }

    React.useEffect(() => {
        setActiveTab('details');
        if (selectedId && isModalOpen) {
            fetchDetail(selectedId);
        } else if (!selectedId && isModalOpen) {
            reset(defaultValues);
            setBookingScope('');
            setPendingDocuments([]);
        }
    }, [selectedId, isModalOpen, reset]);

    // Helper to format slot display label
    const formatSlotLabel = (slot: ISlot) => {
        const date = slot.date && slot.date.length === 8
            ? `${slot.date.substring(0, 4)}-${slot.date.substring(4, 6)}-${slot.date.substring(6, 8)}`
            : slot.date;
        return `${date} | ${slot.startTime} - ${slot.endTime}${slot.isAvailable === false ? ' (Booked)' : ''}`;
    };

    const uniqueDates = React.useMemo(() => {
        const dateSet = new Set<string>();
        const source = bookingsList || [];
        source.forEach((s: any) => { if (s.bookingDate) dateSet.add(s.bookingDate); });
        if ((filters as any).bookingDate) dateSet.add((filters as any).bookingDate as string);
        return Array.from(dateSet).sort();
    }, [bookingsList, (filters as any).bookingDate]);

    const uniqueTimes = React.useMemo(() => {
        const sourceData = bookingsList || [];
        const filteredByDate = (filters as any).bookingDate
            ? sourceData.filter((s: any) => s.bookingDate === (filters as any).bookingDate)
            : sourceData;

        const timeSet = new Set<string>();
        filteredByDate.forEach((s: any) => { if (s.bookingTime) timeSet.add(s.bookingTime); });
        // Always ensure selected time is in the list
        const currentStartTime = (filters as any).bookingTime;
        if (currentStartTime) timeSet.add(currentStartTime);
        return Array.from(timeSet).sort();
    }, [bookingsList, (filters as any).bookingDate, (filters as any).bookingTime]);

    const handleFilterChange = (key: string, value: any) => {
        setFilters({ ...filters, [key]: value || undefined });
    };

    const handleBookingSearch = (searchValues: Record<string, string>) => {
        const newFilters: any = { ...filters };

        bookingSearchFields.forEach((field) => {
            delete newFilters[field.fieldName];
        });

        setFilters({ ...newFilters, ...searchValues });
    };

    return (
        <div>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                >
                    <FieldSearch
                        fields={bookingSearchFields}
                        onSearch={handleBookingSearch}
                    />
                    {!hideAddButton && (
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleClickOpen}
                        >
                            Add Booking
                        </Button>
                    )}
                </Stack>

                <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Filter by Type</InputLabel>
                        <Select
                            value={(filters as any).bookingType || ''}
                            label="Filter by Type"
                            onChange={(e) => {
                                const val = e.target.value;
                                const updatedFilters: any = { ...filters, bookingType: val || undefined };
                                if (val !== 'consultation') updatedFilters.specialistId = undefined;
                                if (val !== 'package') updatedFilters.wellnessPackageId = undefined;
                                setFilters(updatedFilters);
                            }}
                        >
                            <MenuItem value="">All Types</MenuItem>
                            {BOOKING_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Specialist Filter — enabled only when bookingType = consultation */}
                    <Autocomplete
                        size="small"
                        sx={{ minWidth: 220 }}
                        options={specialistFilterOptions}
                        disabled={(filters as any).bookingType !== 'consultation'}
                        value={specialistFilterOptions.find((option) => option.value === ((filters as any).specialistId || '')) || null}
                        onChange={(_event, option) => handleFilterChange('specialistId', option?.value || '')}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        getOptionLabel={(option) => option.label}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Specialist"
                                helperText={(filters as any).bookingType !== 'consultation' ? 'Choose consultation type first' : ''}
                            />
                        )}
                    />

                    {/* Wellness Package Filter — enabled only when bookingType = package */}
                    <Autocomplete
                        size="small"
                        sx={{ minWidth: 220 }}
                        options={packageFilterOptions}
                        disabled={(filters as any).bookingType !== 'package'}
                        value={packageFilterOptions.find((option) => option.value === ((filters as any).wellnessPackageId || '')) || null}
                        onChange={(_event, option) => handleFilterChange('wellnessPackageId', option?.value || '')}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        getOptionLabel={(option) => option.label}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Wellness Package"
                                helperText={(filters as any).bookingType !== 'package' ? 'Choose package type first' : ''}
                            />
                        )}
                    />

                    {/* User Filter */}
                    <Box sx={{ minWidth: 200 }}>
                        <PaginatedSearchDropdown
                            label="Filter by User"
                            value={(filters as any).userId || ''}
                            onChange={(val) => handleFilterChange('userId', val)}
                            fetchOptions={fetchUserOptions}
                            resetKey="grid-user-filter"
                        />
                    </Box>

                    {/* <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Filter by Date</InputLabel>
                        <Select
                            value={(filters as any).bookingDate || ''}
                            label="Filter by Date"
                            onChange={(e) => handleFilterChange('bookingDate', e.target.value)}
                            renderValue={(selected) => {
                                if (!selected) return 'All Dates';
                                const dateStr = selected as string;
                                const formatted = dateStr.length === 8 ? `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}` : dateStr;
                                return dayjs(formatted).format('DD MMM YYYY');
                            }}
                        >
                            <MenuItem value="">All Dates</MenuItem>
                            {uniqueDates.map((date: string) => {
                                const formatted = date.length === 8 ? `${date.substring(0, 4)}-${date.substring(4, 6)}-${date.substring(6, 8)}` : date;
                                return (
                                    <MenuItem key={date} value={date}>
                                        {dayjs(formatted).format('DD MMM YYYY')}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl> */}

                    {/* <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Filter by Time</InputLabel>
                        <Select
                            value={(filters as any).bookingTime || ''}
                            label="Filter by Time"
                            onChange={(e) => handleFilterChange('bookingTime', e.target.value)}
                            renderValue={(selected) => {
                                if (!selected) return 'All Times';
                                return selected as string;
                            }}
                        >
                            <MenuItem value="">All Times</MenuItem>
                            {uniqueTimes.map((t: string) => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </Select>
                    </FormControl> */}

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Filter by Mode</InputLabel>
                        <Select
                            value={(filters as any).serviceMode || ''}
                            label="Filter by Mode"
                            onChange={(e) => handleFilterChange('serviceMode', e.target.value)}
                        >
                            <MenuItem value="">All Modes</MenuItem>
                            {SERVICE_MODES.map((mode) => (
                                <MenuItem key={mode.value} value={mode.value}>{mode.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Filter by Status</InputLabel>
                        <Select
                            value={(filters as any).status || ''}
                            label="Filter by Status"
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                        >
                            <MenuItem value="">All Statuses</MenuItem>
                            {BOOKING_STATUSES.map((status) => (
                                <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Filter by Payment</InputLabel>
                        <Select
                            value={(filters as any).paymentStatus || ''}
                            label="Filter by Payment"
                            onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                        >
                            <MenuItem value="">All Payments</MenuItem>
                            {PAYMENT_STATUSES.map((status) => (
                                <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </Box>

            <Drawer
                anchor="right"
                open={isModalOpen}
                onClose={handleClose}
            >
                <Box sx={{ width: { xs: '100%', sm: 600 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Header */}
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white' }}>
                        <Typography variant="h6">{selectedId ? 'Edit Booking' : 'Add Booking'}</Typography>
                        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {selectedId && (
                        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} aria-label="Booking details tabs" sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                            <Tab label="Booking Details" value="details" id="booking-details-tab" aria-controls="booking-details-panel" />
                            <Tab label="Booking Cycle" value="cycle" id="booking-cycle-tab" aria-controls="booking-cycle-panel" />
                        </Tabs>
                    )}

                    <Box role={selectedId ? 'tabpanel' : undefined} id="booking-details-panel" aria-labelledby={selectedId ? 'booking-details-tab' : undefined} hidden={activeTab !== 'details'} sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={2}>
                                {/* Core Booking Details */}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>Booking Details</Typography>
                                </Grid>

                                {!selectedId && (
                                    <Grid item xs={12} sm={6}>
                                        <Controller
                                            name="bookingScope"
                                            control={control}
                                            rules={{ required: 'Booking scope is required' }}
                                            render={({ field }) => (
                                                <FormControl fullWidth size="small" error={!bookingScope && !!errors.bookingScope}>
                                                    <InputLabel>Booking Scope</InputLabel>
                                                    <Select
                                                        {...field}
                                                        value={bookingScope}
                                                        label="Booking Scope"
                                                        onChange={(e) => {
                                                            const nextScope = e.target.value as 'general' | 'corporate';
                                                            setBookingScope(nextScope);
                                                            field.onChange(nextScope);
                                                            if (nextScope === 'general') {
                                                                setValue('corporateId', undefined);
                                                            }
                                                        }}
                                                    >
                                                        <MenuItem value="general">General</MenuItem>
                                                        <MenuItem value="corporate">Corporate</MenuItem>
                                                    </Select>
                                                    {errors.bookingScope && (
                                                        <FormHelperText>{errors.bookingScope?.message as string}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            )}
                                        />
                                    </Grid>
                                )}

                                {!selectedId && bookingScope === 'corporate' && (
                                    <Grid item xs={12} sm={6}>
                                        <Controller
                                            name="corporateId"
                                            control={control}
                                            rules={{ required: 'Corporate is required for corporate booking' }}
                                            render={({ field }) => (
                                                <PaginatedSearchDropdown
                                                    label="Corporate"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    fetchOptions={fetchCorporateOptions}
                                                    resetKey={`${bookingScope}`}
                                                    disabled={!bookingScope}
                                                    error={!!errors.corporateId}
                                                    helperText={errors.corporateId?.message as string}
                                                />
                                            )}
                                        />
                                    </Grid>
                                )}

                                {!canOpenCreateForm && !selectedId && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">
                                            Select booking scope first to open the booking form.
                                        </Typography>
                                    </Grid>
                                )}

                                {canOpenCreateForm && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <Controller
                                                name="bookingType"
                                                control={control}
                                                rules={{ required: 'Booking type is required' }}
                                                render={({ field }) => (
                                                    <FormControl fullWidth size="small" disabled={!!selectedId} error={!!errors.bookingType}>
                                                        <InputLabel>Booking Type</InputLabel>
                                                        <Select {...field} label="Booking Type">
                                                            {BOOKING_TYPES.map((type) => (
                                                                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                                                            ))}
                                                        </Select>
                                                        {errors.bookingType && (
                                                            <FormHelperText>{errors.bookingType?.message as string}</FormHelperText>
                                                        )}
                                                    </FormControl>
                                                )}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            {selectedId ? (
                                                <TextField
                                                    label="User"
                                                    fullWidth
                                                    size="small"
                                                    value={userLabel || watch('userId') || ''}
                                                    InputProps={{ readOnly: true }}
                                                />
                                            ) : (
                                                <Controller
                                                    name="userId"
                                                    control={control}
                                                    rules={{ required: 'User is required' }}
                                                    render={({ field }) => (
                                                        <PaginatedSearchDropdown
                                                            label="User"
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            fetchOptions={fetchUserOptions}
                                                            resetKey={`${bookingScope}-${watchedCorporateId || ''}`}
                                                            disabled={
                                                                (!bookingScope && !selectedId) ||
                                                                (bookingScope === 'corporate' && !watchedCorporateId && !selectedId)
                                                            }
                                                            error={!!errors.userId}
                                                            helperText={errors.userId?.message as string}
                                                        />
                                                    )}
                                                />
                                            )}
                                        </Grid>

                                        {selectedUser && (
                                            <Grid item xs={12}>
                                                {renderDetailAccordion(
                                                    'User Details',
                                                    <Grid container spacing={1}>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Name:</strong> {selectedUser.name || '-'}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Phone:</strong> {selectedUser.phone || '-'}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Email:</strong> {selectedUser.email || '-'}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Gender:</strong> {selectedUser.gender || '-'}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>,
                                                    true
                                                )}
                                            </Grid>
                                        )}

                                        {selectedDependent && (
                                            <Grid item xs={12}>
                                                {renderDetailAccordion(
                                                    'Dependent Details',
                                                    <Grid container spacing={1}>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Name:</strong> {selectedDependent.name || '-'}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Phone:</strong> {selectedDependent.phone || '-'}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Email:</strong> {selectedDependent.email || '-'}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Gender:</strong> {selectedDependent.gender || '-'}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Relation:</strong> {selectedDependent.relationship || selectedDependent.relation || '-'}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Age:</strong> {selectedDependent.age || '-'}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        )}

                                        {selectedId && (paymentDetail || walletDetail || refundDetail || paymentSummary) && (
                                            <Grid item xs={12}>
                                                {renderDetailAccordion(
                                                    'Payment Details',
                                                    <Grid container spacing={1.5}>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Payment Gateway:</strong> {paymentDetail?.paymentGateway || "-"}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Payment Status:</strong> {paymentDetail?.paymentStatus || watch('paymentStatus') || "-"}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Gateway Amount:</strong> {formatCurrency(paymentSummary?.amountChargedToRazorpay ?? paymentDetail?.finalAmount, paymentDetail?.currency)}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Wallet Amount:</strong> {formatCurrency(paymentSummary?.amountUsedFromWallet ?? walletDetail?.amountUsed, paymentDetail?.currency)}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Refunded Amount:</strong> {formatCurrency(paymentSummary?.amountRefunded ?? refundDetail?.amountRefunded, paymentDetail?.currency)}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Actual Amount Paid:</strong> {formatCurrency(paymentSummary?.actualAmountPaid, paymentDetail?.currency)}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Base Amount:</strong> {formatCurrency(paymentDetail?.baseAmount, paymentDetail?.currency)}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Discount Amount:</strong> {formatCurrency(paymentDetail?.discountAmount, paymentDetail?.currency)}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Coupon Code:</strong> {paymentSummary?.couponCode || paymentDetail?.couponCode || "-"}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Transaction ID:</strong> {paymentDetail?.paymentTransactionId || watch('paymentTransactionId') || "-"}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Razorpay Order ID:</strong> {paymentDetail?.razorpayOrderId || "-"}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Razorpay Payment ID:</strong> {paymentDetail?.razorpayPaymentId || "-"}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Typography variant="body2">
                                                                <strong>Paid At:</strong> {formatDateTime(paymentSummary?.paidAt || paymentDetail?.paidAt)}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        )}

                                        {selectedId && watchedDocuments.length > 0 && (
                                            <Grid item xs={12}>
                                                {renderDetailAccordion(
                                                    'Documents',
                                                    <Grid container spacing={1}>
                                                        {watchedDocuments.map((doc, index) => (
                                                            <Grid item xs={12} key={`${doc.url || doc.fileName || 'document'}-${index}`}>
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center',
                                                                        gap: 2,
                                                                        p: 1,
                                                                        border: 1,
                                                                        borderColor: 'divider',
                                                                        borderRadius: 1,
                                                                        bgcolor: 'background.paper',
                                                                    }}
                                                                >
                                                                    <Box>
                                                                        <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                                                                            {doc.type || 'Document'}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            {doc.fileName || doc.url || '-'}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        component="a"
                                                                        href={resolveDocumentUrl(doc.url)}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                    >
                                                                        View
                                                                    </Button>
                                                                </Box>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                )}
                                            </Grid>
                                        )}

                                        {bookingType === 'package' && (
                                            <Grid item xs={12} sm={6}>
                                                {selectedId ? (
                                                    <TextField
                                                        label="Wellness Package"
                                                        fullWidth
                                                        size="small"
                                                        value={packageLabel || watch('wellnessPackageId') || ''}
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                ) : (
                                                    <Controller
                                                        name="wellnessPackageId"
                                                        control={control}
                                                        rules={{ required: 'Wellness package is required' }}
                                                        render={({ field }) => (
                                                            <PaginatedSearchDropdown
                                                                label="Wellness Package"
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                                fetchOptions={fetchPackageOptions}
                                                                resetKey={`${bookingScope}-${watchedCorporateId || ''}`}
                                                                disabled={
                                                                    (!bookingScope && !selectedId) ||
                                                                    (bookingScope === 'corporate' && !watchedCorporateId && !selectedId)
                                                                }
                                                                error={!!errors.wellnessPackageId}
                                                                helperText={errors.wellnessPackageId?.message as string}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </Grid>
                                        )}

                                        {bookingType === 'consultation' && (
                                            <>
                                                <Grid item xs={12} sm={6}>
                                                    {selectedId ? (
                                                        <TextField
                                                            label="Specialist"
                                                            fullWidth
                                                            size="small"
                                                            value={specialistLabel || watch('specialistId') || ''}
                                                            InputProps={{ readOnly: true }}
                                                        />
                                                    ) : (
                                                        <Controller
                                                            name="specialistId"
                                                            control={control}
                                                            rules={{ required: 'Specialist is required' }}
                                                            render={({ field }) => (
                                                                <PaginatedSearchDropdown
                                                                    label="Specialist"
                                                                    value={field.value}
                                                                    onChange={field.onChange}
                                                                    fetchOptions={fetchSpecialistOptions}
                                                                    resetKey={`${bookingScope}-${watchedCorporateId || ''}`}
                                                                    disabled={
                                                                        (!bookingScope && !selectedId) ||
                                                                        (bookingScope === 'corporate' && !watchedCorporateId && !selectedId)
                                                                    }
                                                                    error={!!errors.specialistId}
                                                                    helperText={errors.specialistId?.message as string}
                                                                />
                                                            )}
                                                        />
                                                    )}
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Controller
                                                        name="primaryConcern"
                                                        control={control}
                                                        rules={{ required: 'Primary concern is required' }}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                label="Primary Concern"
                                                                fullWidth
                                                                multiline
                                                                rows={2}
                                                                size="small"
                                                                error={!!errors.primaryConcern}
                                                                helperText={errors.primaryConcern?.message as string}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Controller
                                                        name="consultationMode"
                                                        control={control}
                                                        rules={{ required: 'Consultation mode is required' }}
                                                        render={({ field }) => (
                                                            <FormControl fullWidth size="small" error={!!errors.consultationMode}>
                                                                <InputLabel>Consultation Mode</InputLabel>
                                                                <Select {...field} label="Consultation Mode">
                                                                    {CONSULTATION_MODES.map((mode) => (
                                                                        <MenuItem key={mode.value} value={mode.value}>{mode.label}</MenuItem>
                                                                    ))}
                                                                </Select>
                                                                {errors.consultationMode && (
                                                                    <FormHelperText>{errors.consultationMode?.message as string}</FormHelperText>
                                                                )}
                                                            </FormControl>
                                                        )}
                                                    />
                                                </Grid>
                                            </>
                                        )}

                                        {/* Surgery fields */}
                                        {bookingType === 'surgery' && (
                                            <>
                                                <Grid item xs={12} sm={6}>
                                                    <Controller
                                                        name="full_name"
                                                        control={control}
                                                        rules={{ required: 'Full name is required' }}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                label="Full Name"
                                                                fullWidth
                                                                size="small"
                                                                error={!!errors.full_name}
                                                                helperText={errors.full_name?.message as string}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Controller
                                                        name="email"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                label="Email"
                                                                type="email"
                                                                fullWidth
                                                                size="small"
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Controller
                                                        name="speciality"
                                                        control={control}
                                                        rules={{ required: 'Speciality is required' }}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                label="Speciality"
                                                                fullWidth
                                                                size="small"
                                                                error={!!errors.speciality}
                                                                helperText={errors.speciality?.message as string}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Controller
                                                        name="concern"
                                                        control={control}
                                                        rules={{ required: 'Concern is required' }}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                label="Concern"
                                                                fullWidth
                                                                multiline
                                                                rows={2}
                                                                size="small"
                                                                error={!!errors.concern}
                                                                helperText={errors.concern?.message as string}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                            </>
                                        )}

                                        {/* Second Opinion fields */}
                                        {bookingType === 'second-opinion' && (
                                            <>
                                                <Grid item xs={12} sm={6}>
                                                    <Controller
                                                        name="full_name"
                                                        control={control}
                                                        rules={{ required: 'Full name is required' }}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                label="Full Name"
                                                                fullWidth
                                                                size="small"
                                                                error={!!errors.full_name}
                                                                helperText={errors.full_name?.message as string}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Controller
                                                        name="email"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                label="Email"
                                                                type="email"
                                                                fullWidth
                                                                size="small"
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Controller
                                                        name="concern"
                                                        control={control}
                                                        rules={{ required: 'Concern is required' }}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                label="Concern"
                                                                fullWidth
                                                                multiline
                                                                rows={2}
                                                                size="small"
                                                                error={!!errors.concern}
                                                                helperText={errors.concern?.message as string}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                            </>
                                        )}

                                        {/* Slot Selection - only for package and consultation */}
                                        {!selectedId && (bookingType === 'package' || bookingType === 'consultation') && (
                                            <Grid item xs={12} sm={6}>
                                                <Controller
                                                    name="slotId"
                                                    control={control}
                                                    rules={{ required: 'Slot is required' }}
                                                    render={({ field }) => (
                                                        <PaginatedSearchDropdown
                                                            label="Select Slot"
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            fetchOptions={fetchSlotOptions}
                                                            resetKey={`${bookingType}-${watchedSpecialistId || ''}-${watchedWellnessPackageId || ''}`}
                                                            disabled={loadingSlots || !isSlotPrerequisiteSelected || availableSlots.length === 0}
                                                            error={!!errors.slotId}
                                                            helperText={errors.slotId?.message as string}
                                                        />
                                                    )}
                                                />
                                                {!isSlotPrerequisiteSelected && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {bookingType === 'consultation' ? 'Select specialist first.' : 'Select package first.'}
                                                    </Typography>
                                                )}
                                                {!loadingSlots && isSlotPrerequisiteSelected && availableSlots.length === 0 && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        No available slots found. Create a slot first.
                                                    </Typography>
                                                )}
                                            </Grid>
                                        )}

                                        <Grid item xs={12}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    mt: 1,
                                                }}
                                            >
                                                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                                                    Documents
                                                </Typography>
                                                <Button size="small" variant="outlined" onClick={addDocumentEntry}>
                                                    Add Document
                                                </Button>
                                            </Box>
                                        </Grid>

                                        {watchedDocuments.length === 0 && pendingDocuments.length === 0 && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2" color="text.secondary">
                                                    No documents added yet.
                                                </Typography>
                                            </Grid>
                                        )}

                                        {watchedDocuments.map((doc, index) => (
                                            <React.Fragment key={`existing-document-${index}`}>
                                                <Grid item xs={12} sm={3}>
                                                    <TextField
                                                        label="Type"
                                                        fullWidth
                                                        size="small"
                                                        value={doc.type || 'report'}
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={5}>
                                                    <TextField
                                                        label="Document URL"
                                                        fullWidth
                                                        size="small"
                                                        value={doc.url || ''}
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <TextField
                                                        label="File Name"
                                                        fullWidth
                                                        size="small"
                                                        value={doc.fileName || ''}
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                </Grid>
                                            </React.Fragment>
                                        ))}

                                        {pendingDocuments.map((doc, index) => (
                                            <React.Fragment key={`pending-document-${index}`}>
                                                <Grid item xs={12} sm={3}>
                                                    <TextField
                                                        select
                                                        label="Type"
                                                        fullWidth
                                                        size="small"
                                                        value={doc.type || 'report'}
                                                        onChange={(e) => updatePendingDocument(index, 'type', e.target.value as PendingBookingDocument['type'])}
                                                    >
                                                        <MenuItem value="report">Report</MenuItem>
                                                        <MenuItem value="prescription">Prescription</MenuItem>
                                                        <MenuItem value="other">Other</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={12} sm={5}>
                                                    <Button
                                                        variant="outlined"
                                                        component="label"
                                                        fullWidth
                                                        sx={{ height: 40, justifyContent: 'flex-start' }}
                                                    >
                                                        {doc.fileName || 'Choose File'}
                                                        <input
                                                            hidden
                                                            type="file"
                                                            onChange={(e) => {
                                                                const file = e.target.files?.[0] || null;
                                                                updatePendingDocument(index, 'file', file);
                                                                updatePendingDocument(index, 'fileName', file?.name || '');
                                                            }}
                                                        />
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <TextField
                                                        label="File Name"
                                                        fullWidth
                                                        size="small"
                                                        value={doc.fileName || ''}
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                </Grid>
                                            </React.Fragment>
                                        ))}

                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>Schedule & Service</Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Controller
                                                name="price"
                                                control={control}
                                                rules={{ required: 'Price is required', min: { value: 0, message: 'Price cannot be negative' } }}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        label="Price"
                                                        type="number"
                                                        fullWidth
                                                        required
                                                        size="small"
                                                        error={!!errors.price}
                                                        helperText={errors.price?.message as string}
                                                    />
                                                )}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Controller
                                                name="bookingDate"
                                                control={control}
                                                rules={{ required: 'Date is required' }}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        type="date"
                                                        label="Booking Date"
                                                        fullWidth
                                                        size="small"
                                                        InputLabelProps={{ shrink: true }}
                                                        error={!!errors.bookingDate}
                                                        helperText={errors.bookingDate?.message as string}
                                                    />
                                                )}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Controller
                                                name="bookingTime"
                                                control={control}
                                                rules={{ required: 'Time is required' }}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        type="time"
                                                        label="Booking Time"
                                                        fullWidth
                                                        size="small"
                                                        InputLabelProps={{ shrink: true }}
                                                        error={!!errors.bookingTime}
                                                        helperText={errors.bookingTime?.message as string}
                                                    />
                                                )}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Controller
                                                name="serviceMode"
                                                control={control}
                                                rules={{ required: 'Service Mode is required' }}
                                                render={({ field }) => (
                                                    <FormControl fullWidth size="small" error={!!errors.serviceMode} required>
                                                        <InputLabel>Service Mode</InputLabel>
                                                        <Select {...field} label="Service Mode">
                                                            {SERVICE_MODES.map((mode) => (
                                                                <MenuItem key={mode.value} value={mode.value}>{mode.label}</MenuItem>
                                                            ))}
                                                        </Select>
                                                        {errors.serviceMode && (
                                                            <FormHelperText>{errors.serviceMode?.message as string}</FormHelperText>
                                                        )}
                                                    </FormControl>
                                                )}
                                            />
                                        </Grid>

                                        {/* Contact Info */}
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>Contact Information</Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Controller
                                                name="contactNo"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        label="Contact No"
                                                        fullWidth
                                                        size="small"
                                                    />
                                                )}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Controller
                                                name="alternateContactNo"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        label="Alt Contact No"
                                                        fullWidth
                                                        size="small"
                                                    />
                                                )}
                                            />
                                        </Grid>

                                        {/* Address - Only if relevant modes */}
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>Service Address</Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Controller
                                                name="serviceAddress.addressLine"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        label="Address Line"
                                                        fullWidth
                                                        required
                                                        size="small"
                                                        error={!!errors.serviceAddress?.addressLine}
                                                        helperText={errors.serviceAddress?.addressLine?.message as string}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm={4}>
                                            <Controller
                                                name="serviceAddress.city"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        label="City"
                                                        fullWidth
                                                        required
                                                        size="small"
                                                        error={!!errors.serviceAddress?.city}
                                                        helperText={errors.serviceAddress?.city?.message as string}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm={4}>
                                            <Controller
                                                name="serviceAddress.state"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        label="State"
                                                        fullWidth
                                                        size="small"
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Controller
                                                name="serviceAddress.pincode"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        label="Pincode"
                                                        fullWidth
                                                        required
                                                        size="small"
                                                        error={!!errors.serviceAddress?.pincode}
                                                        helperText={errors.serviceAddress?.pincode?.message as string}
                                                    />
                                                )}
                                            />
                                        </Grid>


                                        {/* Status & Meta */}
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>Status & Payment</Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Controller
                                                name="status"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Booking Status</InputLabel>
                                                        <Select {...field} label="Booking Status">
                                                            {BOOKING_STATUSES.map((status) => (
                                                                <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Controller
                                                name="paymentStatus"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Payment Status</InputLabel>
                                                        <Select {...field} label="Payment Status">
                                                            {PAYMENT_STATUSES.map((status) => (
                                                                <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Controller
                                                name="notes"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        label="Notes"
                                                        fullWidth
                                                        multiline
                                                        rows={2}
                                                        size="small"
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        {selectedId && (
                                            <Grid item xs={12}>
                                                <Controller
                                                    name="cancellationReason"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            label="Cancellation Reason"
                                                            fullWidth
                                                            multiline
                                                            rows={2}
                                                            size="small"
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        )}
                                    </>
                                )}
                            </Grid>
                        </form>
                    </Box>

                    {selectedId && (
                        <Box role="tabpanel" id="booking-cycle-panel" aria-labelledby="booking-cycle-tab" hidden={activeTab !== 'cycle'} sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
                            {cycleBooking && cycleBooking._id === selectedId ? (
                                <BookingCycle lifecycle={cycleBooking.lifecycle} createdAt={cycleBooking.createdAt} />
                            ) : (
                                <Typography color="text.secondary">Booking-cycle history is unavailable.</Typography>
                            )}
                        </Box>
                    )}

                    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleClose} variant="outlined" color="error">Cancel</Button>
                        {activeTab === 'details' && (
                            <Button onClick={handleSubmit(onSubmit)} variant="contained" color="primary">
                                Submit
                            </Button>
                        )}
                    </Box>
                </Box>
            </Drawer>
        </div>
    );
}
